import React, { useMemo } from 'react';

import { toLabelledEntity } from 'components/inspection/Form/model';
import {
  FormFieldProps,
  SingleChoiceFormFieldProps,
} from 'components/inspection/Form/types';
import { CommonTranslationKeys, FieldTranslationKeys, InspectionTranslationKeys, InspectionTemplateTranslationKeys } from 'intl/keys';
import { getLevelsForSingleSite } from 'presentation/level/levelExtractor';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import {
  useLevelsToSelect,
} from 'store/level';
import { useProcesses } from 'store/process';
import { useSites } from 'store/site';

import { IssueFormPresentational } from './presentational';
import { IssueFormProps, SetableIssueFields } from './types';

function _IssueForm(props: IssueFormProps): React.ReactElement {
  const { processes } = useProcesses();
  const { disabledFields, availableSites, availableLevelsPerSite } = props;

  const {
    items: sites,
  } = useSites();
  const { levelsPerSite } = useLevelsToSelect();

  const processFieldProps: FormFieldProps<SetableIssueFields> = useMemo(
    () => ({
      formKey: 'process',
      required: true,
      labelId: CommonTranslationKeys.process,
      fieldName: 'singleselect-process',
      disabled: disabledFields?.includes('process')
        ? { reason: InspectionTranslationKeys.formIssueProcessEditWarning }
        : false,

      available: processes.map(toLabelledEntity),
      autoSelect: 'asSingleSelect',
      reserveSpaceForHelperText: false,
      dense: false,
      'data-qa': 'issue_process_field',
      issueParams: null,
    }),
    [processes, disabledFields]
  );

  const siteFieldProps: SingleChoiceFormFieldProps<SetableIssueFields> =
    useMemo(
      () => ({
        formKey: 'site',
        required: true,
        labelId: FieldTranslationKeys.site,
        fieldName: 'singleselect-site',
        disabled: disabledFields?.includes('site')
          ? { reason: InspectionTranslationKeys.formIssueSiteEditWarning }
          : false,
        available: availableSites ?? sites.filter(keepUndeleted).map(toLabelledEntity),
        autoSelect: 'asSingleSelect',
        entityName: EditableStandardEntityName.location,
        reserveSpaceForHelperText: false,
        dense: false,
        'data-qa': 'issue_location_field',
        issueParams: null,
      }),
      [sites, disabledFields, availableSites]
    );

  const levelFieldProps: SingleChoiceFormFieldProps<SetableIssueFields> =
    useMemo(
      () => ({
        formKey: 'level',
        required: true,
        labelId: FieldTranslationKeys.level,
        fieldName: 'singleselect-level',
        available: availableLevelsPerSite ?? levelsPerSite,
        dependsOn: 'site',
        extractor: getLevelsForSingleSite,
        autoSelect: 'asSingleSelect',
        entityName: EditableStandardEntityName.level,
        reserveSpaceForHelperText: false,
        dense: false,
        'data-qa': 'issue_level_field',
        issueParams: null,
      }),
      [availableLevelsPerSite, levelsPerSite]
    );

  const titleFieldProps: FormFieldProps<SetableIssueFields> = useMemo(
    () => ({
      formKey: 'title',
      required: true,
      labelId: InspectionTemplateTranslationKeys.wizardSummary,
      fieldName: 'text-field-title',
      localStorageKey: 'issue-summary',
      reserveSpaceForHelperText: false,
      dense: false,
      'data-qa': 'issue_summary_field',
      issueParams: null,
    }),
    []
  );

  return (
    <IssueFormPresentational
      processFieldProps={processFieldProps}
      siteFieldProps={siteFieldProps}
      levelFieldProps={levelFieldProps}
      titleFieldProps={titleFieldProps}
    />
  );
}

export const IssueForm = _IssueForm;
